'use client'

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import {
  Badge,
  Box,
  Flex,
  Grid,
  HStack,
  IconButton,
  Text,
} from '@chakra-ui/react'
import type { ColumnDef, PaginationState } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { Eye } from 'lucide-react'
import {
  useGetDonorCardDetailQuery,
  useGetDonorDetailQuery,
  useGetDonorLabStudiesQuery,
} from '@entities/donor/api'
import {
  DONOR_LAB_STUDY_SCOPE_FILTER,
  DONOR_LAB_STUDY_SCOPES,
  EDonorStatusStep,
} from '@entities/donor/model'
import type {
  IDonorLabStudiesSummary,
  IDonorLabStudyItem,
  TDonorLabStudyScope,
} from '@entities/donor/model'
import { LabAdmissionDecisionActions } from '@features/lab-admission-decision'
import { AppCard } from '@/shared/ui/card'
import { DataTable } from '@/shared/ui/data-table'
import { getLabResearchBadgeColors } from '../model/lab-research'
import {
  hasActiveStep,
  isDonorCardReadOnly,
} from '../model/use-donor-card-read-only'

interface Props {
  visitId: string
  basePath?: string
}

/**
 * Recap of the admission verdict for the current visit, plus the two decision
 * actions while the donor is still waiting on the therapist.
 */
function AdmissionSummaryCard({
  summary,
  donorId,
  canDecide,
}: {
  summary: IDonorLabStudiesSummary
  donorId: string
  /** The workflow sits at UNDER_THERAPIST_REVIEW — the verdict is due now. */
  canDecide: boolean
}) {
  const { t } = useTranslation()
  const palette = getLabResearchBadgeColors(summary.admission_decision)

  const rows: { label: string; value: string }[] = [
    {
      label: t('donor_detail.lab_research.summary.decided_by'),
      value: summary.admission_decided_by || '—',
    },
    {
      label: t('donor_detail.lab_research.summary.decided_at'),
      value: summary.admission_decided_at
        ? dayjs(summary.admission_decided_at).format('DD.MM.YYYY  HH:mm')
        : '—',
    },
    {
      label: t('donor_detail.lab_research.summary.reason'),
      value: summary.admission_reason || '—',
    },
  ]

  return (
    <AppCard.Root>
      <Box p={5}>
        <Flex align="center" gap={3} mb={4} wrap="wrap">
          <Text fontSize="sm" color="fg.muted">
            {t('donor_detail.lab_research.summary.title')}
          </Text>
          <Badge
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="11px"
            fontWeight="medium"
            bg={palette.bg}
            color={palette.color}
          >
            {summary.admission_decision_label ||
              t('donor_detail.lab_research.summary.pending')}
          </Badge>
        </Flex>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
          {rows.map((row) => (
            <Box key={row.label}>
              <Text fontSize="xs" color="fg.muted" mb={1}>
                {row.label}
              </Text>
              <Text fontSize="sm">{row.value}</Text>
            </Box>
          ))}
        </Grid>

        <Box mt={5} pt={5} borderTopWidth="1px" borderColor="border">
          {canDecide ? (
            <LabAdmissionDecisionActions
              donorId={donorId}
              canSetAdmission={canDecide}
            />
          ) : (
            <Text fontSize="sm" color="fg.muted" textAlign="right">
              {summary.all_analyses_confirmed
                ? t('donor_detail.lab_research.decision.locked')
                : t('donor_detail.lab_research.decision.awaiting_results')}
            </Text>
          )}
        </Box>
      </Box>
    </AppCard.Root>
  )
}

export function LabResearchTab({ visitId, basePath = '/donors' }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [scope, setScope] = useState<TDonorLabStudyScope>('all')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // The tab is mounted under the donor card, which already loaded this query —
  // it resolves the visit to the donor the lab studies are keyed by.
  const { data: donorDetail } = useGetDonorDetailQuery(visitId)
  const donorId = donorDetail?.donor_id ?? ''

  // The verdict belongs to the workflow step, not to the lab results: the two
  // actions show exactly while the donor card sits at UNDER_THERAPIST_REVIEW.
  const { data: donorCard } = useGetDonorCardDetailQuery(donorId)
  const canDecide =
    hasActiveStep(donorCard, EDonorStatusStep.UNDER_THERAPIST_REVIEW) &&
    !isDonorCardReadOnly(donorCard)

  const { data, isLoading, isFetching } = useGetDonorLabStudiesQuery({
    donor_id: donorId,
    filter: DONOR_LAB_STUDY_SCOPE_FILTER[scope],
    page: pagination.pageIndex,
    size: pagination.pageSize,
  })

  const items = data?.data ?? []
  const summary = data?.summary ?? null

  // A shrinking result set (scope switch, deleted rows) can leave the pager past
  // the last page — step back so the table doesn't sit on an empty page.
  useEffect(() => {
    const totalPages = data?.totalPages ?? 0
    if (totalPages > 0 && pagination.pageIndex > totalPages - 1) {
      setPagination((p) => ({ ...p, pageIndex: totalPages - 1 }))
    }
  }, [data?.totalPages, pagination.pageIndex])

  const openDetail = (item: IDonorLabStudyItem) =>
    navigate({
      to: `${basePath}/${visitId}/lab-research/${item.id}` as never,
    })

  const columns = useMemo<ColumnDef<IDonorLabStudyItem>[]>(() => {
    const textCell = (content: React.ReactNode, muted: boolean) => (
      <Text
        py={3}
        px={4}
        fontSize="sm"
        color={muted ? 'fg.subtle' : 'fg.muted'}
        whiteSpace="nowrap"
      >
        {content}
      </Text>
    )

    return [
      {
        id: 'number',
        header: () => t('donor_detail.lab_research.table.number'),
        cell: (info) =>
          textCell(
            info.row.index + 1 + pagination.pageIndex * pagination.pageSize,
            !info.row.original.is_current_visit,
          ),
      },
      {
        accessorKey: 'order_number',
        header: () => t('donor_detail.lab_research.table.order_number'),
        cell: (info) =>
          textCell(
            info.getValue<string>() || '—',
            !info.row.original.is_current_visit,
          ),
      },
      {
        accessorKey: 'date',
        header: () => t('donor_detail.lab_research.table.submitted_at'),
        cell: (info) => {
          const value = info.getValue<string | null>()
          return textCell(
            value ? dayjs(value).format('DD.MM.YYYY  HH:mm') : '—',
            !info.row.original.is_current_visit,
          )
        },
      },
      {
        id: 'analyses',
        header: () => t('donor_detail.lab_research.table.analyses'),
        cell: (info) => {
          const analyses = info.row.original.analyses ?? []
          if (!analyses.length)
            return textCell('—', !info.row.original.is_current_visit)

          return (
            <HStack gap={1.5} py={3} px={4} wrap="wrap">
              {analyses.map((analysis) => {
                const palette = getLabResearchBadgeColors(analysis.status)
                return (
                  <Badge
                    key={`${analysis.loinc_code}-${analysis.name}`}
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontSize="11px"
                    fontWeight="medium"
                    bg={palette.bg}
                    color={palette.color}
                    opacity={info.row.original.is_current_visit ? 1 : 0.5}
                    title={
                      analysis.result
                        ? `${analysis.status_label}: ${analysis.result}`
                        : analysis.status_label
                    }
                  >
                    {analysis.name}
                  </Badge>
                )
              })}
            </HStack>
          )
        },
      },
      {
        accessorKey: 'laboratory',
        header: () => t('donor_detail.lab_research.table.laboratory'),
        cell: (info) =>
          textCell(
            info.getValue<string>() || '—',
            !info.row.original.is_current_visit,
          ),
      },
      {
        accessorKey: 'lab_technician',
        header: () => t('donor_detail.lab_research.table.laborant'),
        cell: (info) =>
          textCell(
            info.getValue<string | null>() || '—',
            !info.row.original.is_current_visit,
          ),
      },
      {
        accessorKey: 'admission_status',
        header: () => t('donor_detail.lab_research.table.admission_status'),
        cell: (info) => {
          const row = info.row.original
          const palette = getLabResearchBadgeColors(row.admission_status)
          return (
            <Box py={3} px={4}>
              <Badge
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="11px"
                fontWeight="medium"
                bg={palette.bg}
                color={palette.color}
              >
                {row.admission_status_label || row.admission_status || '—'}
              </Badge>
            </Box>
          )
        },
      },
      {
        id: 'view',
        header: () => t('donor_detail.lab_research.table.view'),
        cell: (info) => (
          <Box py={2} px={4}>
            <IconButton
              variant="ghost"
              size="xs"
              color="fg.muted"
              aria-label={t('donor_detail.lab_research.table.view')}
              onClick={(event) => {
                event.stopPropagation()
                openDetail(info.row.original)
              }}
              _hover={{ color: 'fg', bg: 'bg.muted' }}
            >
              <Eye size={15} />
            </IconButton>
          </Box>
        ),
      },
    ]
  }, [t, pagination.pageIndex, pagination.pageSize])

  return (
    <Flex direction="column" gap={5}>
      <AppCard.Root>
        <Box p={5} pb={0}>
          <HStack gap={2} wrap="wrap">
            {DONOR_LAB_STUDY_SCOPES.map((option) => {
              const active = option === scope
              return (
                <Box
                  key={option}
                  as="button"
                  onClick={() => {
                    setScope(option)
                    setPagination((p) => ({ ...p, pageIndex: 0 }))
                  }}
                  px={4}
                  py={2}
                  fontSize="sm"
                  fontWeight={active ? 'semibold' : 'medium'}
                  borderRadius="lg"
                  bg={active ? 'primary.solid' : 'transparent'}
                  color={active ? 'white' : 'fg.muted'}
                  borderWidth="1px"
                  borderColor={active ? 'primary.fg' : 'transparent'}
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={
                    !active ? { color: 'fg', borderColor: 'border' } : undefined
                  }
                >
                  {t(`donor_detail.lab_research.scope.${option}`)}
                  {active && data?.totalSize != null && (
                    <Text
                      as="span"
                      ml={1.5}
                      fontSize="xs"
                      fontWeight="semibold"
                      opacity={0.85}
                    >
                      {data.totalSize}
                    </Text>
                  )}
                </Box>
              )
            })}
          </HStack>
        </Box>

        <Box p={5} pt={4}>
          <DataTable<IDonorLabStudyItem>
            data={items}
            columns={columns}
            isLoading={isLoading || isFetching}
            pagination={pagination}
            onPaginationChange={setPagination}
            manualPagination
            rowCount={data?.totalSize ?? 0}
            onRowClick={openDetail}
            emptyLabel={t('donor_detail.lab_research.empty')}
            renderPaginationLabel={(start, end, total) =>
              t('dashboard.pagination.showing', { start, end, total })
            }
          />
        </Box>
      </AppCard.Root>

      {summary && (
        <AdmissionSummaryCard
          summary={summary}
          donorId={donorId}
          canDecide={canDecide}
        />
      )}
    </Flex>
  )
}
